import React, { memo, FunctionComponent } from 'react';
import { Form, Field } from 'react-final-form';
import { Box } from 'mdlkit';
import { useIntl, FormattedMessage } from 'react-intl';
import PasswordField from '../PasswordField';
import DOBField from './DOBField';
import EmailField from './EmailField';
import LegalLink from './LegalLink';
import {
  email,
  required,
  composeValidators,
} from '../../../utils/shared/validations';
import { password, validateDob } from '../../../utils/register/validations';
import { event } from '../../../lib/analytics';
import { StyledForm, FormSubmitLoadingButton } from '../../shared/Styles';
import { PatientSubmitError, DobType } from '../../../interfaces/register';
import {
  REGISTRATION,
  CREATE_ACCOUNT,
  ATTEMPT,
} from '../../../constants/events';

interface ValuesType {
  password?: string;
  dobMonth?: string;
  dobDay?: string;
  dobYear?: string;
}

const validateRecord = (values: ValuesType) => {
  const errors: {
    password?: string;
  } & DobType = {};

  return {
    ...errors,
    ...validateDob(values),
  };
};

interface Props {
  onSubmit: (values: any) => Promise<void | PatientSubmitError>;
  // PPW-6512 — DOB carried from the coverage-check flow. `initialValues` seeds the field; when
  // `lockIdentity` is true the DOB renders read-only (email/password stay editable).
  initialValues?: { dobMonth?: string; dobDay?: string; dobYear?: string };
  lockIdentity?: boolean;
}

const PatientForm: FunctionComponent<Props> = ({
  onSubmit,
  initialValues,
  lockIdentity = false,
}: Props) => {
  const { formatMessage: f } = useIntl();

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialValues}
      validate={validateRecord}
      render={({
        handleSubmit,
        submitting,
        hasSubmitErrors,
        dirtySinceLastSubmit,
        values,
      }) => (
        <StyledForm onSubmit={handleSubmit} spellCheck={false}>
          <Field
            id="email"
            label={f({ id: 'email' })}
            name="email"
            validate={composeValidators(required, email)}
            parse={(value) => value && value.trim()}
            component={EmailField}
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
          />

          <Field
            id="password"
            label={f({ id: 'password' })}
            name="password"
            validate={password}
            values={values}
            component={PasswordField}
          />

          <DOBField
            placeholder={f({ id: 'register.dateOfBirth.placeholder' })}
            disabled={lockIdentity}
          />

          <Box mt="sm">
            <LegalLink />
          </Box>

          <FormSubmitLoadingButton
            id="createAccountBtn"
            type="submit"
            size="large"
            onClick={() =>
              event({
                action: CREATE_ACCOUNT,
                category: REGISTRATION,
                parameters: { status: ATTEMPT },
              })
            }
            disabled={submitting || (hasSubmitErrors && !dirtySinceLastSubmit)}
            isLoading={submitting}
            data-track-event
            data-track={JSON.stringify({
              controlName: 'create-account-main-button',
              controlText: f({
                id: 'register.createAccount',
              }),
            })}
          >
            <FormattedMessage id="register.createAccount" />
          </FormSubmitLoadingButton>
        </StyledForm>
      )}
    />
  );
};

export default memo(PatientForm);
